import { baseApi } from "@/api/baseApi";
import type { SnapshotGridQueryResponse } from "@/modules/snapshots/businessReports.api";
import type {
  SnapshotView,
  SnapshotViewAccess,
  SnapshotViewConfiguration,
  SnapshotViewGridQueryPayload,
  SnapshotViewListResponse,
  SnapshotViewSortField,
} from "@/modules/snapshots/snapshotViews.types";
import { SNAPSHOT_VIEW_TAGS } from "@/modules/snapshots/snapshotViews.constraints";

const timezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const snapshotViewsApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getSnapshotViews: builder.query<
      SnapshotViewListResponse,
      {
        query?: string;
        offset?: number;
        limit?: number;
        sort_field?: SnapshotViewSortField;
        sort_direction?: "asc" | "desc";
      } | void
    >({
      providesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEWS],
      query: args => ({
        url: "/pf-mainweb/business-reports/snapshot-views",
        params: args || undefined,
      }),
    }),
    getSnapshotView: builder.query<SnapshotView, string>({
      providesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEWS],
      query: id => ({ url: `/pf-mainweb/business-reports/snapshot-views/${id}` }),
    }),
    getSnapshotViewFavorites: builder.query<SnapshotView[], void>({
      providesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES],
      query: () => ({ url: "/pf-mainweb/business-reports/snapshot-views/favorites" }),
    }),
    addSnapshotViewFavorite: builder.mutation<void, SnapshotView>({
      invalidatesTags: (_result, error) => (error ? [] : [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES]),
      query: view => ({
        url: "/pf-mainweb/business-reports/snapshot-views/favorites",
        method: "POST",
        data: { snapshot_view_id: view.id },
      }),
      async onQueryStarted(view, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          snapshotViewsApi.util.updateQueryData("getSnapshotViewFavorites", undefined, favorites => {
            if (!favorites.some(favorite => favorite.id === view.id)) {
              favorites.unshift(view);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    deleteSnapshotViewFavorite: builder.mutation<void, string>({
      invalidatesTags: (_result, error) => (error ? [] : [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES]),
      query: snapshotViewId => ({
        url: `/pf-mainweb/business-reports/snapshot-views/${snapshotViewId}/favorites`,
        method: "DELETE",
      }),
      async onQueryStarted(snapshotViewId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          snapshotViewsApi.util.updateQueryData("getSnapshotViewFavorites", undefined, favorites =>
            favorites.filter(favorite => favorite.id !== snapshotViewId),
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getSnapshotViewGridRows: builder.query<
      SnapshotGridQueryResponse,
      { snapshotViewId: string; payload: SnapshotViewGridQueryPayload }
    >({
      providesTags: ["BusinessReportsOutcome"],
      query: ({ snapshotViewId, payload }) => ({
        url: `/pf-mainweb/business-reports/snapshot-views/${snapshotViewId}/grid-query`,
        method: "POST",
        params: { client_timezone: timezone() },
        data: payload,
      }),
    }),
    createSnapshotView: builder.mutation<
      SnapshotView,
      { name: string; snapshot_collection_id: string; configuration: SnapshotViewConfiguration }
    >({
      invalidatesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEWS, SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES],
      query: data => ({ url: "/pf-mainweb/business-reports/snapshot-views", method: "POST", data }),
    }),
    updateSnapshotView: builder.mutation<
      SnapshotView,
      {
        id: string;
        revision: number;
        name?: string;
        access?: SnapshotViewAccess;
        configuration?: SnapshotViewConfiguration;
      }
    >({
      invalidatesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEWS, SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES],
      query: ({ id, ...data }) => ({
        url: `/pf-mainweb/business-reports/snapshot-views/${id}`,
        method: "PATCH",
        data,
      }),
    }),
    deleteSnapshotView: builder.mutation<void, string>({
      invalidatesTags: [SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEWS, SNAPSHOT_VIEW_TAGS.SNAPSHOT_VIEW_FAVORITES],
      query: id => ({ url: `/pf-mainweb/business-reports/snapshot-views/${id}`, method: "DELETE" }),
    }),
  }),
});

export const {
  useAddSnapshotViewFavoriteMutation,
  useCreateSnapshotViewMutation,
  useDeleteSnapshotViewFavoriteMutation,
  useDeleteSnapshotViewMutation,
  useGetSnapshotViewFavoritesQuery,
  useGetSnapshotViewGridRowsQuery,
  useGetSnapshotViewQuery,
  useGetSnapshotViewsQuery,
  useUpdateSnapshotViewMutation,
} = snapshotViewsApi;
